import { useEffect, useState } from 'react';
import { messagingApi } from '../../services/messaging-api';
import { ConfirmationModal } from '../shared/ConfirmationModal';
import { ProviderForm } from './ProviderForm';

type MessagingProvidersPageProps = {
  tenantId?: string;
  onNavigate?: (page: string) => void;
};

export function MessagingProvidersPage({ tenantId = '', onNavigate }: MessagingProvidersPageProps) {
  const [providers, setProviders] = useState<any[]>([]);
  const [providerTypes, setProviderTypes] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteProviderCandidate, setDeleteProviderCandidate] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    loadProviders();
  }, [tenantId]);

  const loadProviders = async () => {
    try {
      const [providersRes, providerTypesRes] = await Promise.all([
        messagingApi.getProviders(tenantId),
        messagingApi.getProviderTypes(),
      ]);
      setProviders(providersRes.data);
      setProviderTypes(providerTypesRes.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProvider = () => {
    setEditingProvider(null);
    setShowForm(true);
  };

  const handleEditProvider = (provider: any) => {
    setEditingProvider(provider);
    setShowForm(true);
  };

  const handleFormSuccess = async () => {
    setShowForm(false);
    setEditingProvider(null);
    await loadProviders();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingProvider(null);
  };

  const handleDelete = (id: string, name: string) => {
    setDeleteProviderCandidate({ id, name });
  };

  const confirmDeleteProvider = async () => {
    if (!deleteProviderCandidate) return;
    try {
      await messagingApi.deleteProvider(deleteProviderCandidate.id);
      await loadProviders();
      setDeleteProviderCandidate(null);
    } catch (err) {
      setError('Failed to delete provider');
      setDeleteProviderCandidate(null);
    }
  };

  return (
    <div className="p-6">
      <div className="rounded-3xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-950 overflow-hidden">
        <div className="flex flex-col gap-6 p-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Messaging Providers</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Manage provider credentials and defaults for your messaging channels.</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => onNavigate?.('message-batches')}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
            >
              Batches
            </button>

            <button
              onClick={handleAddProvider}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Add Provider
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-6 mb-4 rounded-2xl bg-red-100 px-4 py-3 text-red-700 dark:bg-red-900/40 dark:text-red-200 border border-red-400 dark:border-red-700">
          {error}
        </div>
      )}

      {showForm && (
        <div className="mx-6 mb-6 rounded-3xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold">{editingProvider ? 'Edit Provider' : 'New Provider'}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Add or update a messaging provider for this tenant.</p>
            </div>
            <button
              type="button"
              onClick={handleFormCancel}
              className="text-gray-600 dark:text-gray-300 hover:text-gray-900"
            >
              Cancel
            </button>
          </div>

          <ProviderForm
            tenantId={tenantId}
            providerId={editingProvider?.id}
            provider={editingProvider || undefined}
            providers={providerTypes}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </div>
      )}

      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {providers.map((provider) => (
            <div
              key={provider.id}
              className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-950 flex justify-between items-center"
            >
              <div>
                <h3 className="font-bold">{provider.name}</h3>
                <p className="text-sm text-gray-600">
                  {provider.providerType?.name} • {provider.senderId}
                </p>

                {provider.isDefault && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                    Default
                  </span>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleEditProvider(provider)}
                  className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(provider.id, provider.name)}
                  className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {deleteProviderCandidate && (
        <ConfirmationModal
          title="Delete Provider"
          message={`Are you sure you want to delete provider \"${deleteProviderCandidate.name}\"? This cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
          onConfirm={confirmDeleteProvider}
          onClose={() => setDeleteProviderCandidate(null)}
        />
      )}
    </div>
  );
}